import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Attribution {
  id: string
  channel: string
  model_type: string
  attributed_conversions: number
  attributed_revenue: number
  contribution_percent: number
  avg_time_to_conversion: number
  touchpoints: number
}

interface Model {
  id: string
  name: string
  type: string
  status: string
  accuracy: number
  total_conversions_analyzed: number
  last_calculated: string
}

interface Statistics {
  total_channels: number
  total_conversions: number
  total_attributed_revenue: number
  avg_touchpoints: number
  active_models: number
  avg_time_to_conversion_days: number
  top_channel: string
  attribution_coverage: number
}

export default function AttributionModelingPage() {
  const [attributions, setAttributions] = useState<Attribution[]>([])
  const [models, setModels] = useState<Model[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'attribution' | 'models'>('attribution')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [attrRes, modelsRes, statsRes] = await Promise.all([
        fetch('/api/attributionmodeling/attributions'),
        fetch('/api/attributionmodeling/models'),
        fetch('/api/attributionmodeling/statistics')
      ])

      if (attrRes.ok) setAttributions(await attrRes.json())
      if (modelsRes.ok) setModels(await modelsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching attribution modeling data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getModelTypeBadge = (type: string) => {
    const classes: Record<string, string> = {
      first_touch: 'bg-blue-100 text-blue-800',
      last_touch: 'bg-purple-100 text-purple-800',
      linear: 'bg-green-100 text-green-800',
      time_decay: 'bg-orange-100 text-orange-800',
      position_based: 'bg-pink-100 text-pink-800',
      data_driven: 'bg-indigo-100 text-indigo-800'
    }
    return classes[type] || 'bg-gray-100 text-gray-800'
  }

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      calculating: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Attribution Modeling</h1>
          <p className="mt-2 text-gray-600">Multi-touch attribution and channel performance analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Conversions</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_conversions.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Attributed Revenue</p>
              <p className="text-2xl font-bold text-green-600">${statistics.total_attributed_revenue.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Touchpoints</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_touchpoints.toFixed(1)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Channel</p>
              <p className="text-2xl font-bold text-purple-600 capitalize">{statistics.top_channel}</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('attribution')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'attribution' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Channel Attribution ({attributions.length})
            </button>
            <button
              onClick={() => setActiveTab('models')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'models' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Models ({models.length})
            </button>
          </nav>
        </div>

        {activeTab === 'attribution' && (
          <div className="space-y-4">
            {attributions.map((attr) => (
              <div key={attr.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">{attr.channel.replace('_', ' ')}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${getModelTypeBadge(attr.model_type)}`}>
                      {attr.model_type.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">${attr.attributed_revenue.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">{attr.attributed_conversions.toLocaleString()} conversions</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Contribution</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${attr.contribution_percent * 100}%` }}></div>
                      </div>
                      <span className="text-sm font-bold text-blue-600">{(attr.contribution_percent * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Avg Time to Conversion</p>
                    <p className="text-lg font-bold text-gray-900">{attr.avg_time_to_conversion.toFixed(1)} days</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Touchpoints</p>
                    <p className="text-lg font-bold text-purple-600">{attr.touchpoints}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'models' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Calculated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {models.map((model) => (
                  <tr key={model.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{model.name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getModelTypeBadge(model.type)}`}>
                        {model.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{(model.accuracy * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4 text-gray-500">{model.total_conversions_analyzed.toLocaleString()}</td>
                    <td className="px-6 py-4 text-gray-500">{new Date(model.last_calculated).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(model.status)}`}>
                        {model.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
